import { useEffect, useState } from "react";
import { Phone, Fence, Images, User, Mail, ArrowRight, Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Leistungen", href: "#leistungen", id: "nav-link-services", icon: Fence },
  { label: "Projekte", href: "#projekte", id: "nav-link-gallery", icon: Images },
  { label: "Über uns", href: "#ueber-uns", id: "nav-link-about", icon: User },
  { label: "Kontakt", href: "#kontakt", id: "nav-link-contact", icon: Mail },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[box-shadow,border-color,background-color] duration-300 ${
        scrolled
          ? "bg-[#F9F8F6]/80 backdrop-blur-xl border-b border-[#E5E2DC] shadow-lg shadow-[#244A32]/5"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div
        className={`px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between transition-[height] duration-300 ${
          scrolled ? "h-14" : "h-20"
        }`}
      >
        <a href="#start" className="flex items-center" data-testid="nav-brand-link">
          <img
            src="/logo.jpg"
            alt="LLP Zaun&Garten Logo"
            className={`w-auto rounded-md transition-[height] duration-300 ${scrolled ? "h-9" : "h-12"}`}
            data-testid="nav-logo"
          />
        </a>

        <nav className="hidden md:flex items-center gap-1 bg-white/70 border border-[#E5E2DC] rounded-full p-1.5" data-testid="main-nav">
          {NAV_LINKS.map(({ label, href, id, icon: Icon }) => (
            <a
              key={href}
              href={href}
              data-testid={id}
              className="group relative flex items-center gap-2 text-sm font-medium text-[#575953] hover:text-[#244A32] px-4 py-2 rounded-full transition-colors"
            >
              <Icon className="w-4 h-4 text-[#C89D66] group-hover:text-[#244A32] transition-colors" />
              {label}
              <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full bg-[#244A32] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+4917641162064"
            data-testid="header-phone-link"
            className={`hidden sm:flex items-center gap-2 text-sm font-semibold transition-colors ${
              scrolled ? "text-[#244A32] hover:text-[#1D3B28]" : "text-white hover:text-[#C89D66]"
            }`}
          >
            <span className={`w-8 h-8 rounded-full flex items-center justify-center ${scrolled ? "bg-[#244A32]/10" : "bg-white/15 backdrop-blur-md"}`}>
              <Phone className="w-4 h-4" />
            </span>
            0176 41162064
          </a>
          <a
            href="#termin"
            data-testid="header-cta-booking"
            className="group bg-[#244A32] hover:bg-[#1D3B28] text-white text-sm font-semibold pl-5 pr-4 py-2.5 rounded-full transition-colors hidden sm:flex items-center gap-2"
          >
            Termin anfragen
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
          <button
            type="button"
            aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            className={`md:hidden w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              scrolled || menuOpen ? "bg-[#244A32]/10 text-[#244A32]" : "bg-white/15 backdrop-blur-md text-white"
            }`}
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="md:hidden bg-[#F9F8F6]/95 backdrop-blur-xl border-b border-[#E5E2DC] shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col">
            {NAV_LINKS.map(({ label, href, id, icon: Icon }) => (
              <a
                key={href}
                href={href}
                data-testid={`mobile-${id}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3.5 rounded-xl text-base font-semibold text-[#1A1C19] hover:bg-[#244A32]/5 transition-colors"
              >
                <Icon className="w-5 h-5 text-[#C89D66]" />
                {label}
              </a>
            ))}
            <a
              href="tel:+4917641162064"
              data-testid="mobile-header-phone-link"
              className="flex items-center gap-3 px-3 py-3.5 rounded-xl text-base font-semibold text-[#244A32] hover:bg-[#244A32]/5 transition-colors"
            >
              <Phone className="w-5 h-5" />
              0176 41162064
            </a>
            <a
              href="#termin"
              data-testid="mobile-cta-booking"
              onClick={() => setMenuOpen(false)}
              className="mt-2 bg-[#244A32] text-white text-center font-bold py-3.5 rounded-full"
            >
              Termin anfragen
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
